import { HttpClient, HttpContext } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { UnsubscribeController, ImageFile, ImageServerFile } from '@personal-area/global/common'
import { IS_PRIVATE_API } from '@personal-area/global/http/models';
import {
  Observable,
  catchError,
  finalize,
  from,
  fromEvent,
  map,
  mergeMap,
  switchMap,
  takeUntil,
} from 'rxjs';

import {
  DEFAULT_FILE_SIZE,
  DEFAULT_HEIGHT,
  DEFAULT_QUALITY,
  DEFAULT_WIDTH,
  ErrorType,
  FILE_TYPE,
  FileAttachSettings,
  MIME_TYPES,
} from './attachment';

@Directive()
export class AbstractFileAttachComponent
  extends UnsubscribeController
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input()
  public set settings(config: FileAttachSettings) {
    this._settings = { ...this._settings, ...config };
  }

  public get settings(): FileAttachSettings {
    return this._settings;
  }

  @Input() modificator = '';
  @Input() uploadText = '';
  @Input() ctrl = new UntypedFormControl();

  // ui
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef | undefined;
  @ViewChild('dropArea', { static: true }) dropArea: ElementRef | undefined;

  @Output() ctrlChanged = new EventEmitter<void>();
  @Output() errorOccurred = new EventEmitter<string>();

  allowedFileTypes = '';
  mimeType = MIME_TYPES;
  files: ImageFile[] = [];

  protected _settings: FileAttachSettings = {
    allowedContentTypes: [
      MIME_TYPES.jpg,
      MIME_TYPES.png,
      MIME_TYPES.bmp,
      MIME_TYPES.pdf,
    ],
    allowedFileSize: DEFAULT_FILE_SIZE, // 5mb
    maxFiles: 3,
    dimensions: { compress: { width: 1280, height: 1280 } },
  };
  protected _processingFiles = false;

  constructor(
    private _sanitizer: DomSanitizer,
    private _http: HttpClient,
    private _ngZone: NgZone,
    protected _cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.allowedFileTypes = this.settings.allowedContentTypes
      ? this.settings.allowedContentTypes
          .map((type) => FILE_TYPE[type])
          .join(', ')
      : '';

    let ctrlValidators = [
      this._maxFilesValidator(),
      this._fileTypeDimensionsSizeLoadValidator(),
      this._processingFilesValidator(),
    ];
    if (this.ctrl?.validator) {
      ctrlValidators = [this.ctrl.validator].concat(ctrlValidators);
    }
    this.ctrl?.setValidators(ctrlValidators);
  }

  public ngAfterViewInit(): void {
    const el = this.dropArea?.nativeElement;
    const hasDragAPI =
      'draggable' in el || ('ondragstart' in el && 'ondrop' in el);
    if (!hasDragAPI) {
      return;
    }

    this._ngZone.runOutsideAngular(() => {
      const events = ['dragover', 'dragenter', 'dragleave', 'drop'];
      from(events)
        .pipe(
          mergeMap((event) => fromEvent(el, event) as Observable<DragEvent>),
          takeUntil(this._unsubscribe),
        )
        .subscribe((e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();

          if (e.type === 'drop') {
            this._ngZone.run(() => this.attachFiles(e.dataTransfer));
          }
        });
    });
  }

  public attachFiles(target: EventTarget | DataTransfer | null): void {
    const files = (target as HTMLInputElement).files;
    if (files) {
      this._processingFiles = true;
      this.ctrl?.markAsDirty();

      Array.from(files).forEach((file: File) => {
        if (file.name.toLowerCase().includes('bmp')) {
          file = new File([file], file.name, { type: MIME_TYPES.bmp });
        }
        this._generateImageFile(
          file,
          file.type === MIME_TYPES.pdf &&
            file.size > this.settings.allowedFileSize,
          !this.settings.allowedContentTypes.includes(file.type as MIME_TYPES),
        );
      });
      this._processingFiles = false;
    }
    this._clearFileInput();
    this._cdRef.markForCheck();
  }

  public onImageFileDelete(file: ImageFile): void {
    this._clearFileInput();

    file.isLoaded = false;
    this.files = this.files.filter((_file: ImageFile) => _file.id !== file.id);

    const valueToPatch = this.files.length
      ? this._convertFilesForServer(this.files)
      : null;
    valueToPatch
      ? this.ctrl?.patchValue(
          this.settings.maxFiles === 1 ? valueToPatch[0] : valueToPatch,
        )
      : this._clearFakeFileInput();

    this._cdRef.markForCheck();
  }

  public hasFileErrors(file: ImageFile): boolean {
    return Object.values(file.errors || []).some((error) => error);
  }

  public handleFileWithServerUpload(file: ImageFile): void {
    file.isLoading = true;
    this._updFileCtrl(file, this.files);

    const data = this._convertFilesForServer([file]);

    this._uploadToServer(this.settings.uploadUrl || '', data[0])
      .pipe(finalize(() => (file.isLoading = false)))
      .subscribe({
        next: (serverId) => {
          file.serverId = serverId as string;
          file.isLoaded = true;
          if (file.errors) {
            file.errors.load = false;
          }
          this._updFileCtrl(file, this.files);
        },
        error: () => {
          if (file.errors) {
            file.errors.load = true;
          }
          file.isLoaded = false;
          this._updFileCtrl(file, this.files);
        },
      });
  }

  protected _onImageLoadFail(file: ImageFile): void {
    file.isLoading = false;
    if (file.errors) {
      file.errors.load = true;
    }
    this._updFileCtrl(file, this.files);

    this.errorOccurred.next(ErrorType.FailedToRenderPreview);
    this._cdRef.markForCheck();
  }

  protected _compressImage(
    img: HTMLImageElement,
    file: File,
    imageFile: ImageFile,
  ): Observable<string> {
    return new Observable<string>((observer) => {
      const reader = new FileReader();
      reader.onload = (e): void => {
        const exifOrientation = this._getExifOrientation(
          (e.target as FileReader).result as ArrayBuffer,
        );

        let w = img.width;
        let h = img.height;
        let x;
        let y;
        let canvasSize;

        const { dimensions } = this.settings;
        const crop = dimensions?.crop;

        if (crop) {
          canvasSize = Math.max(
            Math.min(img.width, img.height, crop.max.height),
            crop.min.height,
          );

          x = (canvasSize - img.width) / 2;
          y = (canvasSize - img.height) / 2;
        } else {
          const maxWidth = dimensions?.compress?.width || DEFAULT_WIDTH;
          const maxHeight = dimensions?.compress?.height || DEFAULT_HEIGHT;

          const scale =
            exifOrientation > 4
              ? Math.min(maxHeight / w, maxWidth / h, 1)
              : Math.min(maxWidth / w, maxHeight / h, 1);
          h = Math.round(h * scale);
          w = Math.round(w * scale);
        }

        // resize the canvas and draw the image data into it
        const canvas = this._imgToCanvasWithOrientation(
          img,
          w,
          h,
          exifOrientation,
          x,
          y,
          canvasSize,
          canvasSize,
        );

        // get the data from canvas as 70% JPG (can be also PNG, etc.)
        observer.next(canvas.toDataURL('image/jpeg', DEFAULT_QUALITY));
        observer.complete();
      };
      reader.onerror = (): void => {
        this._onImageLoadFail(imageFile);
        observer.error();
      };
      reader.readAsArrayBuffer(file);
    });
  }

  protected _updFileCtrl(file: ImageFile, value?: ImageFile[]): void {
    if (this.files.some((_file) => _file.id === file.id)) {
      this.files = this.files.map((_file) =>
        _file.id === file.id ? file : _file,
      );
    } else {
      this.files.push(file);
    }

    if (this.settings.maxFiles === 1) {
      this.files = [this.files[this.files.length - 1]];
    }

    const valueToPatch = this._convertFilesForServer(
      value ? value : this.files,
    );
    this.ctrl?.patchValue(
      this.settings.maxFiles === 1 ? valueToPatch[0] : valueToPatch,
    );
    this.ctrlChanged.next();
    this._cdRef.markForCheck();
  }

  protected _convertFilesForServer(files: ImageFile[]): ImageServerFile[] {
    return files.map(
      (_file: ImageFile): ImageServerFile => ({
        id: _file.id,
        serverId: _file.serverId,
        extension: _file.nativeFile.name.split('.').pop() || '',
        content: _file.content,
        fileContent:
          _file.imageSrc &&
          _file.imageSrc.replace(
            /^data:(image|application)\/(png|jpg|jpeg|svg|psd|gif|pdf|bmp);base64,/,
            '',
          ),
        fileName: _file.nativeFile.name,
        groupId: _file.groupId,
        option: _file.option,
      }),
    );
  }

  protected _uploadToServer(
    url: string,
    data: ImageServerFile,
  ): Observable<string | void> {
    if (this.settings.needsBlob) {
      if (data.content) {
        const formData = new FormData();
        formData.append(data.id, data.content, data.fileName);

        return this._http
          .post<{ files: { id: string; fileName: string; name: string }[] }>(
            url,
            formData,
            { context: new HttpContext().set(IS_PRIVATE_API, true) },
          )
          .pipe(
            map((response) => response.files[0].id),
            catchError(() =>
              this._http
                .post<{ fileId: string }>(
                  this.settings.uploadFallbackUrl || '',
                  data,
                  { context: new HttpContext().set(IS_PRIVATE_API, true) },
                )
                .pipe(map((response) => response.fileId)),
            ),
          ) as Observable<string>;
      }

      return this._http
        .post<{ fileId: string }>(this.settings.uploadFallbackUrl || '', data, {
          context: new HttpContext().set(IS_PRIVATE_API, true),
        })
        .pipe(map((response) => response.fileId));
    }

    return this._http
      .post<{ fileId: string }>(url, data, {
        context: new HttpContext().set(IS_PRIVATE_API, true),
      })
      .pipe(
        map((response: { fileId: string }) => response.fileId),
      ) as Observable<string>;
  }

  private _maxFilesValidator(): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      return this.files.length > this.settings.maxFiles
        ? { maxFiles: true }
        : null;
    };
  }

  private _processingFilesValidator(): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      return this._processingFiles ? { processingFiles: true } : null;
    };
  }

  private _fileTypeDimensionsSizeLoadValidator(): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      return this.files.some(
        (file) =>
          file.errors?.type ||
          file.errors?.dimensions ||
          file.errors?.size ||
          file.errors?.load,
      )
        ? { invalidFileFound: true }
        : null;
    };
  }

  protected _clearFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private _clearFakeFileInput(): void {
    this.ctrl?.patchValue(null);
    this.ctrlChanged.next();
  }

  private _generateId(): string {
    return Math.random().toString(36).substring(7);
  }

  protected _generateImageFile(
    file: File,
    isSizeInvalid: boolean,
    isTypeInvalid: boolean,
  ): void {
    const imageFile: ImageFile = {
      id: this._generateId(),
      nativeFile: file,
      content: null,
      errors: {
        size: isSizeInvalid,
        type: isTypeInvalid,
        dimensions: false,
        load: false,
      },
      isLoading: true,
    };

    this._updFileCtrl(imageFile, this.files);

    const reader: FileReader = new FileReader();
    reader.onload = (e): void => {
      if (!reader.result) {
        // MDN: The value is null if the reading is not yet complete or was unsuccessful.
        this.errorOccurred.next(ErrorType.FileReaderResultNull);
        this._onImageLoadFail(imageFile);
        return;
      }

      imageFile.imageSrc = reader.result as string;
      imageFile.nativeFile.type === MIME_TYPES.pdf
        ? this._renderIcon(imageFile, file)
        : this._renderPreview(
            imageFile,
            (e.target as FileReader).result as string,
            file,
          );
    };

    reader.onerror = (): void => this._onImageLoadFail(imageFile);

    reader.readAsDataURL(file);
  }

  private _renderIcon(file: ImageFile, nativeFile: File): void {
    this._handleFile(file, nativeFile);
  }

  protected _renderPreview(
    file: ImageFile,
    src: string,
    nativeFile: File,
  ): void {
    const image = new Image();
    image.src = src;

    image.onload = (): void => {
      const crop = this.settings.dimensions?.crop;
      if (crop && file.errors) {
        file.errors.dimensions =
          image.width < crop.min.width || image.height < crop.min.height;
        if (file.errors.dimensions) {
          file.imageSrc = '';

          this._handleFile(file);
          return;
        }
      }

      // compress images with size more than 0.5Mb
      if (nativeFile.size > 524288 || this.settings.dimensions?.crop) {
        this._compressImage(image, nativeFile, file)
          .pipe(
            switchMap((img) => {
              file.imageSrc = img;
              return this._fetch(img);
            }),
          )
          .subscribe({
            next: (data) => this._handleFile(file, data),
            error: () => this._handleFile(file),
          });
        return;
      }

      this._handleFile(file, nativeFile);
    };

    image.onerror = (): void => this._onImageLoadFail(file);
  }

  // -2 = not jpeg, -1 = no data, 1..8 = orientations
  private _getExifOrientation(src: ArrayBuffer): number {
    const view = new DataView(src);
    if (view.getUint16(0, false) !== 0xffd8) {
      return -2;
    }
    const length = view.byteLength;
    let offset = 2;

    while (offset < length) {
      const marker = view.getUint16(offset, false);
      offset += 2;
      if (marker === 0xffe1) {
        if (view.getUint32((offset += 2), false) !== 0x45786966) {
          return -1;
        }
        const little = view.getUint16((offset += 6), false) === 0x4949;
        offset += view.getUint32(offset + 4, little);
        const tags = view.getUint16(offset, little);
        offset += 2;
        for (let i = 0; i < tags; i++) {
          if (view.getUint16(offset + i * 12, little) === 0x0112) {
            return view.getUint16(offset + i * 12 + 8, little);
          }
        }
        // tslint:disable-next-line:no-bitwise
      } else if ((marker & 0xff00) !== 0xff00) {
        break;
      } else {
        offset += view.getUint16(offset, false);
      }
    }
    return -1;
  }

  private _imgToCanvasWithOrientation(
    img: HTMLImageElement,
    rawWidth: number,
    rawHeight: number,
    orientation: number,
    x = 0,
    y = 0,
    canvasWidth?: number,
    canvasHeight?: number,
  ): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    if (orientation > 4) {
      canvas.width = canvasHeight || rawHeight;
      canvas.height = canvasWidth || rawWidth;
    } else {
      canvas.width = canvasWidth || rawWidth;
      canvas.height = canvasHeight || rawHeight;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.errorOccurred.next(ErrorType.FailedToCreateContext);
      return canvas;
    }

    switch (orientation) {
      case 2:
        ctx.transform(-1, 0, 0, 1, rawWidth, 0);
        break;
      case 3:
        ctx.transform(-1, 0, 0, -1, rawWidth, rawHeight);
        break;
      case 4:
        ctx.transform(1, 0, 0, -1, 0, rawHeight);
        break;
      case 5:
        ctx.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6:
        ctx.transform(0, 1, -1, 0, rawHeight, 0);
        break;
      case 7:
        ctx.transform(0, -1, -1, 0, rawHeight, rawWidth);
        break;
      case 8:
        ctx.transform(0, -1, 1, 0, 0, rawWidth);
        break;
    }
    ctx.drawImage(img, x, y, rawWidth, rawHeight);
    return canvas;
  }

  protected _handleFile(file: ImageFile, nativeFile?: File | Blob): void {
    file.secureImageSrc = this._sanitizer.bypassSecurityTrustResourceUrl(
      file.imageSrc || '',
    );

    if (file.errors?.size || file.errors?.dimensions || file.errors?.type) {
      file.isLoading = false;
      this.errorOccurred.next(
        'file-attach.error.file.' +
          (file.errors.type
            ? 'type'
            : file.errors.dimensions
            ? 'dimensions'
            : 'size'),
      );
      this._updFileCtrl(file);

      return;
    }

    if (this.settings.uploadUrl && !this.settings.needsBlob) {
      this.handleFileWithServerUpload(file);

      return;
    }

    if (this.settings.needsBlob) {
      this._handleFileWithBlobContent(file, nativeFile);

      return;
    }

    file.isLoading = false;
    this._updFileCtrl(file);
  }

  private _handleFileWithBlobContent(
    file: ImageFile,
    nativeFile: File | Blob | undefined,
  ): void {
    file.content = nativeFile || null;
    if (this.settings.uploadUrl) {
      this.handleFileWithServerUpload(file);
    } else {
      file.isLoading = false;
      this._updFileCtrl(file);
    }
  }

  // there is no other way to crossbrowserly get compressed blob ( canvas.toBlob has low support for quality setting )
  private _fetch(input: string): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.open('GET', input);
      xhr.send();

      xhr.onload = (): void => {
        observer.next(xhr.response);
        observer.complete();
      };

      xhr.ontimeout = (): void =>
        observer.error(new Error('Network request timeout'));
      xhr.onerror = (): void =>
        observer.error(new Error('Network request failed'));
      xhr.onabort = (): void => observer.complete();

      return (): void => observer.complete();
    });
  }
}
